package com.essntl.features.proposal.ui.addeditproposal.components

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.selection.SelectionContainer
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.BasicAlertDialog
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import com.essntl.core.utils.utils.formatIntMoney
import com.essntl.core.utils.viewmodel.State
import com.essntl.features.currency.domain.repository.CurrencyRepository
import com.essntl.features.currency.ui.EsCurrencyPriceInput
import com.essntl.features.price.domain.model.proposalserviceprice.ProposalServicePriceModel
import com.essntl.features.price.domain.repository.PriceRepository
import com.essntl.features.profile.domain.model.ProfileModel
import com.essntl.features.proposal.domain.model.ProposalServiceModel
import com.essntl.uikit.components.button.outline.EsSmallOutlineButton
import com.essntl.uikit.components.button.primary.EsLargePrimaryButton
import com.essntl.uikit.components.button.secondary.EsSmallSecondaryButton
import com.essntl.uikit.components.dropdown.EsInputDropdownMenu
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.scroll.EsVerticalScrollbar
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.toggle.EsPrimarySwitch
import com.essntl.uikit.components.radiobutton.EsPrimaryRadioButton
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.EsRowItem2
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography
import core.validation.EsInputState
import kotlinx.coroutines.launch
import org.koin.compose.koinInject
import core.config.CurrencyConfig

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun SetDynamicPriceDialog(
    isOpen: Boolean,
    isDynamicPrice: Boolean,
    currentRole: ProfileModel.Role?,
    currentProposalService: ProposalServiceModel,
    onDismissRequest: () -> Unit,
    onSave: (ps: ProposalServiceModel, prices: List<ProposalServicePriceModel>) -> Unit,
) {
    var proposalService by remember { mutableStateOf<ProposalServiceModel>(currentProposalService) }
    var getProposalServicePrice by remember { mutableStateOf<State<List<ProposalServicePriceModel>>>(State()) }
    var updateProposalServicePrices by remember { mutableStateOf<State<List<ProposalServicePriceModel>>>(State()) }

    var priceInputState by remember { mutableStateOf(EsInputState.Default) }
    var costInputState by remember { mutableStateOf(EsInputState.Default) }

    // dynamicPriceEnabled is storing the state of the switch
    // so the web proposal knows which price to show. For now we only use custom price
    // but we show the user the prices calculated by the system

    // TODO: PRICING uncomment to ship
//    var customPrice by remember { mutableStateOf(currentProposalService.dynamicPriceEnabled == false) }
//    var systemPrice by remember { mutableStateOf(currentProposalService.dynamicPriceEnabled == true) }
    var customPrice = true
    var systemPrice = false

    var openExplanationDialogForPrice by remember { mutableStateOf<ProposalServicePriceModel?>(null) }

    // --- Dynamic Price Row Selection State ---
    var selectedPriceRowIndex by remember { mutableStateOf<Int?>(null) }

    val scope = rememberCoroutineScope()

    val currencyRepository = koinInject<CurrencyRepository>()
    val priceRepository = koinInject<PriceRepository>()

    fun validatePrice(price: Int? = proposalService.price) =
        if (price == null || price < 0) {
            priceInputState = EsInputState.Error
            false
        } else {
            priceInputState = EsInputState.Default
            true
        }

    fun validateCost(cost: Int? = proposalService.cost) =
        if (cost == null || cost < 0) {
            costInputState = EsInputState.Error
            false
        } else {
            costInputState = EsInputState.Default
            true
        }

    LaunchedEffect(isOpen) {
        if (isOpen) {
            proposalService = currentProposalService
            priceInputState = EsInputState.Default
            costInputState = EsInputState.Default
            updateProposalServicePrices = State()

            // TODO: PRICING uncomment to ship
//            customPrice = currentProposalService.dynamicPriceEnabled == false
//            systemPrice = currentProposalService.dynamicPriceEnabled == true

            if (isDynamicPrice) {
                getProposalServicePrice = getProposalServicePrice.copy(
                    data = emptyList(),
                    isLoading = true,
                )

                priceRepository
                    .getPricesByProposalService(currentProposalService.id)
                    .onSuccess {
                        getProposalServicePrice = getProposalServicePrice.copy(
                            data = it,
                            isLoading = false,
                        )
                    }.onFailure {
                        getProposalServicePrice = getProposalServicePrice.copy(
                            isLoading = false,
                        )
                    }
            }
        }
    }

    if (isOpen) {
        BasicAlertDialog(
            onDismissRequest = onDismissRequest,
            properties =
                DialogProperties(
                    usePlatformDefaultWidth = false,
                ),
            modifier = Modifier.fillMaxWidth(0.4f),
        ) {
            Column(
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.Start,
                modifier = Modifier
                    .fillMaxWidth(0.4f)
                    .clip(RoundedCornerShape(25.dp))
                    .background(color = MaterialTheme.esColorScheme.bgPrimary),
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.SpaceBetween,
                    modifier =
                        Modifier
                            .fillMaxWidth()
                            .padding(
                                vertical = 1.spacing,
                                horizontal = 1.spacing,
                            ),
                ) {
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Text(
                            text = proposalService.service.title,
                            style = MaterialTheme.esTypography.h3Bold24,
                            color = MaterialTheme.esColorScheme.textPrimary,
                        )
                    }

                    EsSmallOutlineIconButton(
                        onClick = onDismissRequest,
                        iconRes = EsIcons.Close,
                    )
                }

                Box(
                    modifier =
                        Modifier
                            .padding(1.spacing)
                            .background(
                                color = MaterialTheme.esColorScheme.bgSecondary,
                                shape = RoundedCornerShape(25.dp),
                            )
                            .clip(RoundedCornerShape(25.dp)),
                ) {
                    Column(
                        verticalArrangement = Arrangement.Center,
                        horizontalAlignment = Alignment.Start,
                        modifier = Modifier.padding(1.spacing),
                    ) {
                        Row(
                            horizontalArrangement = Arrangement.SpaceBetween,
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            Text(
                                text = "Custom price",
                                style = MaterialTheme.esTypography.p1Regular16,
                                color = MaterialTheme.esColorScheme.textPrimary,
                            )

//                            if (getProposalServicePrice.data.orEmpty().isNotEmpty())
//                                EsPrimarySwitch(
//                                    checked = customPrice,
//                                    onCheckedChange = {
//                                        customPrice = !customPrice
//                                        systemPrice = !customPrice
//                                    },
//                                )
                        }

                        Spacer(modifier = Modifier.height(1.5.spacing))

                        Row(
                            horizontalArrangement = Arrangement.spacedBy(1.spacing),
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            EsCurrencyPriceInput(
                                value = (proposalService.price ?: 0) / 100.0,
                                onValueChange = {
                                    val price = (it * 100).toInt()
                                    validatePrice(price)

                                    proposalService = proposalService.copy(
                                        price = price,
                                    )
                                },
                                code = proposalService.priceCurrency?.code ?: CurrencyConfig.defaultCurrencyCode,
                                onCodeChange = {
                                    scope.launch {
                                        currencyRepository.getByCode(it)
                                            .onSuccess { c ->
                                                proposalService = proposalService.copy(priceCurrency = c)
                                            }
                                    }
                                },
                                label = "Selling price",
                                state = priceInputState,
                                enabled = customPrice,
                                modifier = Modifier.weight(1f),
                            )

                            EsCurrencyPriceInput(
                                value = (proposalService.cost ?: 0) / 100.0,
                                onValueChange = {
                                    val cost = (it * 100).toInt()
                                    validateCost(cost)
                                    proposalService = proposalService.copy(cost = cost)
                                },
                                code = proposalService.costCurrency?.code ?: CurrencyConfig.defaultCurrencyCode,
                                onCodeChange = {
                                    scope.launch {
                                        currencyRepository.getByCode(it)
                                            .onSuccess { c ->
                                                proposalService = proposalService.copy(costCurrency = c)
                                            }
                                    }
                                },
                                label = "Cost",
                                state = costInputState,
                                enabled = customPrice,
                                modifier = Modifier.weight(1f),
                            )
                        }
                    }
                }

                if (isDynamicPrice)
                    if (getProposalServicePrice.isLoading ||
                        getProposalServicePrice.data == null
                    )
                        Box(
                            contentAlignment = Alignment.Center,
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            EsSpinner(
                                modifier = Modifier.align(Alignment.Center),
                            )
                        }
                    else if (getProposalServicePrice.data
                            .orEmpty()
                            .isNotEmpty()
                    )
                        Box(
                            modifier =
                                Modifier
                                    .padding(1.spacing)
                                    .background(
                                        color = MaterialTheme.esColorScheme.bgSecondary,
                                        shape = RoundedCornerShape(25.dp),
                                    )
                                    .clip(RoundedCornerShape(25.dp)),
                        ) {
                            Column(
                                verticalArrangement = Arrangement.Center,
                                horizontalAlignment = Alignment.Start,
                                modifier = Modifier.padding(1.spacing),
                            ) {
                                Row(
                                    horizontalArrangement = Arrangement.SpaceBetween,
                                    modifier = Modifier.fillMaxWidth().padding(1.spacing),
                                ) {
                                    Text(
                                        text = "System price",
                                        style = MaterialTheme.esTypography.p1Regular16,
                                        color = MaterialTheme.esColorScheme.textPrimary,
                                    )

//                                    EsPrimarySwitch(
//                                        checked = systemPrice,
//                                        onCheckedChange = {
//                                            systemPrice = !systemPrice
//                                            customPrice = !systemPrice
//                                        },
//                                    )
                                }

                                val lazyListState = rememberLazyListState()

                                Row(
                                    modifier = Modifier.fillMaxWidth(),
                                ) {
                                    LazyColumn(
                                        state = lazyListState,
                                        modifier = Modifier
                                            .clip(RoundedCornerShape(EsConstants.RoundedCornerSize))
                                            .background(
                                                color = MaterialTheme.esColorScheme.bgSecondaryHover.copy(
//                                                    alpha = if (systemPrice) 1f else 0.4f,
                                                    alpha = 1f,
                                                ),
                                            )
                                            .wrapContentHeight(),
                                    ) {
                                        itemsIndexed(
                                            getProposalServicePrice.data.orEmpty(),
                                            key = { _, p -> p.id },
                                        ) { index, price ->
                                            Row(
                                                verticalAlignment = Alignment.CenterVertically,
                                                modifier = Modifier
                                                    .fillMaxWidth()
                                                    .padding(1.spacing)
                                                    .clickable {
                                                        selectedPriceRowIndex = index
                                                        val roundedPrice = kotlin.math.ceil((price.total?.toDouble() ?: 0.0) / 100.0).toInt()
                                                        proposalService = proposalService.copy(price = roundedPrice * 100)
                                                    },
                                            ) {
                                                Column(
                                                    verticalArrangement = Arrangement.spacedBy(1.spacing),
                                                    horizontalAlignment = Alignment.Start,
                                                    modifier = Modifier.weight(3f, fill = true)
                                                ) {
                                                    Text(
                                                        text = price.supplierServicePrice.description,
                                                        color = MaterialTheme.esColorScheme.textPrimary,
                                                        style = MaterialTheme.esTypography.s2SemiBold16,
                                                        maxLines = 1,
                                                        overflow = TextOverflow.Ellipsis,
                                                    )

                                                    Text(
                                                        text = price.supplierServicePrice.supplierService?.supplier?.prettyName.orEmpty(),
                                                        color = MaterialTheme.esColorScheme.textPrimary,
                                                        style = MaterialTheme.esTypography.p2Regular14,
                                                        maxLines = 1,
                                                        overflow = TextOverflow.Ellipsis,
                                                    )
                                                }
                                                Text(
                                                    text = "${CurrencyConfig.defaultCurrencySymbol}${formatIntMoney(price.total?.toInt() ?: 0)}",
                                                    color = MaterialTheme.esColorScheme.textPrimary,
                                                    style = MaterialTheme.esTypography.s2SemiBold16,
                                                    maxLines = 1,
                                                    overflow = TextOverflow.Ellipsis,
                                                    modifier = Modifier
                                                        .align(Alignment.CenterVertically)
                                                        .padding(horizontal = 12.dp)
                                                )
                                                EsSmallOutlineButton(
                                                    text = "?",
                                                    onClick = { openExplanationDialogForPrice = price },
                                                    modifier = Modifier.align(Alignment.CenterVertically)
                                                )
                                                Spacer(modifier = Modifier.weight(1f, fill = true))
                                                EsPrimaryRadioButton(
                                                    selected = selectedPriceRowIndex == index,
                                                    onSelectedChange = { checked ->
                                                        if (checked) {
                                                            selectedPriceRowIndex = index
                                                            val roundedPrice = kotlin.math.ceil((price.total?.toDouble() ?: 0.0) / 100.0).toInt()
                                                            proposalService = proposalService.copy(price = roundedPrice * 100)
                                                        }
                                                    },
                                                    enabled = true,
                                                    modifier = Modifier.align(Alignment.CenterVertically)
                                                )
                                            }
                                        }
                                    }
                                }

                                if (openExplanationDialogForPrice != null) {
                                    AlertDialog(
                                        onDismissRequest = { openExplanationDialogForPrice = null },
                                        confirmButton = {},
                                        dismissButton = {},
                                        text = {
                                            Box(
                                                modifier = Modifier
                                                    .wrapContentHeight()
                                                    .background(
                                                        color = MaterialTheme.esColorScheme.bgPrimary,
                                                        shape = RoundedCornerShape(16.dp)
                                                    )
                                                    .padding(20.dp)
                                            ) {
                                                Text(
                                                    text = openExplanationDialogForPrice?.explanation.orEmpty(),
                                                    style = MaterialTheme.esTypography.p2Regular14,
                                                    color = MaterialTheme.esColorScheme.textPrimary,
                                                )
                                                EsSmallOutlineIconButton(
                                                    onClick = { openExplanationDialogForPrice = null },
                                                    iconRes = EsIcons.Close,
                                                    modifier = Modifier.align(Alignment.TopEnd)
                                                )
                                            }
                                        },
                                        containerColor = Color.Transparent,
                                    )
                                }
                            }
                        }

                Box(
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    EsLargePrimaryButton(
                        text = "Save",
                        onClick = {
                            if (customPrice)
                                onSave(
                                    proposalService.copy(
                                        dynamicPriceEnabled = false,
                                    ),
                                    emptyList(),
                                )
                            else if (systemPrice)
                                scope.launch {
                                    updateProposalServicePrices = updateProposalServicePrices.copy(
                                        data = emptyList(),
                                        isLoading = true,
                                    )

                                    priceRepository
                                        .updateProposalServicePrices(getProposalServicePrice.data.orEmpty())
                                        .onSuccess {
                                            updateProposalServicePrices = updateProposalServicePrices.copy(
                                                data = it,
                                                isLoading = false,
                                            )

                                            // TODO: PRICING uncomment to ship
//                                            onSave(
//                                                proposalService.copy(
//                                                    dynamicPriceEnabled = true,
//                                                ),
//                                                it,
//                                            )
                                        }
                                        .onFailure {
                                            updateProposalServicePrices = updateProposalServicePrices.copy(
                                                data = emptyList(),
                                                isLoading = false,
                                            )
                                        }
                                }
                        },
                        loading = updateProposalServicePrices.isLoading,
                        modifier = Modifier
                            .align(Alignment.BottomEnd)
                            .padding(
                                end = 3.spacing,
                                bottom = 3.spacing,
                            ),
                    )
                }
            }
        }
    }
}